import { useState, useCallback } from 'react';

/**
 * Идентификаторы колонок журнала пользователя.
 */
export type ColumnId =
  | 'idx'
  | 'name'
  | 'producer'
  | 'expiry'
  | 'serial'
  | 'qty'
  | 'qtyPcs'
  | 'price'
  | 'totalSum'
  | 'manager'
  | 'category'
  | 'rxotc'
  | 'code'
  | 'barcode'
  | 'comment'
  | 'recordDate'
  | 'actions';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  required?: boolean;
  width: string;
}

export const ALL_COLUMNS: ColumnDef[] = [
  { id: 'idx',        label: '№',             required: true,  width: '44px' },
  { id: 'name',       label: 'Наименование',  required: true,  width: 'minmax(180px,2fr)' },
  { id: 'producer',   label: 'Производитель',                  width: 'minmax(120px,1fr)' },
  { id: 'expiry',     label: 'Срок годности', required: true,  width: '110px' },
  { id: 'serial',     label: 'Серия',                          width: '110px' },
  { id: 'qty',        label: 'Кол-во уп.',    required: true,  width: '80px' },
  { id: 'qtyPcs',     label: 'Кол-во шт.',                     width: '80px' },
  { id: 'price',      label: 'Цена',                           width: '90px' },
  { id: 'totalSum',   label: 'Сумма',         required: true,  width: '100px' },
  { id: 'manager',    label: 'Менеджер',                       width: '120px' },
  { id: 'category',   label: 'Категория',                      width: '110px' },
  { id: 'rxotc',      label: 'RX/OTC',                         width: '70px' },
  { id: 'code',       label: 'Код',                            width: '90px' },
  { id: 'barcode',    label: 'ШК',                             width: '110px' },
  { id: 'comment',    label: 'Комментарий',                    width: '140px' },
  { id: 'recordDate', label: 'Дата записи',                    width: '108px' },
  { id: 'actions',    label: '',              required: true,  width: '72px' },
];

const LS_KEY = 'user_journal_visible_columns_v1';

const persist = (next: Set<ColumnId>) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
};

const defaultVisible = (): Set<ColumnId> => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as ColumnId[];
      if (Array.isArray(arr) && arr.length > 0) return new Set(arr);
    }
  } catch { /* ignore */ }
  // По умолчанию скрываем: ШК, Кем создан, Ценовая группа
  return new Set<ColumnId>([
    'idx', 'name', 'producer', 'expiry', 'serial',
    'qty', 'qtyPcs', 'totalSum', 'manager', 'category', 'rxotc',
    'recordDate', 'actions',
  ]);
};

export interface UseColumnVisibilityReturn {
  visibleColumns: Set<ColumnId>;
  visibleDefs: ColumnDef[];
  gridTemplate: string;
  toggle: (id: ColumnId) => void;
  showAll: () => void;
  reset: () => void;
}

export const useColumnVisibility = (): UseColumnVisibilityReturn => {
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(defaultVisible);

  const toggle = useCallback((id: ColumnId) => {
    const col = ALL_COLUMNS.find(c => c.id === id);
    if (col?.required) return;
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persist(next);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    const next = new Set<ColumnId>(ALL_COLUMNS.map(c => c.id));
    persist(next);
    setVisibleColumns(next);
  }, []);

  const reset = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    setVisibleColumns(defaultVisible());
  }, []);

  const visibleDefs = ALL_COLUMNS.filter(c => visibleColumns.has(c.id));
  const gridTemplate = visibleDefs.map(c => c.width).join(' ');

  return { visibleColumns, visibleDefs, gridTemplate, toggle, showAll, reset };
};
